import styled from 'styled-components';
import { GlobalToken } from 'antd';

export const InputContainer = styled.div<{ $isEmpty?: boolean; theme: { token: GlobalToken } }>`
  padding: ${({ theme: { token }, $isEmpty }) => ($isEmpty ? '0' : `${token.paddingSM}px 0`)};
  background-color: ${({ theme: { token } }) => token.colorBgLayout};
  width: 100%;
  max-width: 1200px;
`;

export const InputWrapper = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  gap: ${({ theme: { token } }) => token.marginSM}px;
  align-items: center;
`;

export const TextAreaWrapper = styled.div<{ $isEmpty?: boolean; theme: { token: GlobalToken } }>`
  position: relative;
  flex: 1;
  display: flex;
  align-items: ${({ $isEmpty }) => ($isEmpty ? 'flex-start' : 'flex-end')};
`;

export const TextArea = styled.textarea<{ $isEmpty?: boolean; theme: { token: GlobalToken } }>`
  width: 100%;
  background-color: ${({ theme: { token } }) => token.colorFillQuaternary};
  border: 1px solid rgba(105, 215, 240, 1);
  border-radius: ${({ theme: { token } }) => token.borderRadius}px;
  padding: ${({ theme: { token }, $isEmpty }) =>
    $isEmpty
      ? `10px ${token.paddingLG + 40}px 10px ${token.paddingLG}px`
      : `8px ${token.padding + 40}px 8px ${token.padding}px`};
  color: ${({ theme: { token } }) => token.colorText};
  font-size: ${({ theme: { token }, $isEmpty }) => ($isEmpty ? token.fontSizeLG : token.fontSize)}px;
  font-family: ${({ theme: { token } }) => token.fontFamily};
  resize: none;
  min-height: 40px;
  max-height: ${({ $isEmpty }) =>
    $isEmpty ? '180px' : '216px'}; /* 9 lines: 20px * 9 = 180px (empty), 24px * 9 = 216px (regular) */
  overflow-y: auto;
  overflow-x: hidden;
  transition: border-color 0.3s ease, box-shadow 0.3s ease, height 0.1s ease;
  box-shadow: 0 0 0 1px rgba(105, 215, 240, 0.3), 0 0 8px rgba(105, 215, 240, 0.2);
  line-height: ${({ $isEmpty }) => ($isEmpty ? '20px' : '24px')};
  box-sizing: border-box;

  &::placeholder {
    color: ${({ theme: { token } }) => token.colorTextPlaceholder};
    line-height: ${({ $isEmpty }) => ($isEmpty ? '20px' : '24px')};
  }

  /* Scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme: { token } }) => token.colorBorderSecondary};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme: { token } }) => token.colorTextTertiary};
  }

  &:focus {
    outline: none;
    border-color: rgba(105, 215, 240, 1);
    box-shadow: 0 0 0 1px rgba(105, 215, 240, 0.5), 0 0 12px rgba(105, 215, 240, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

export const SendButton = styled.button<{ disabled: boolean; $isEmpty?: boolean; theme: { token: GlobalToken } }>`
  position: absolute;
  right: ${({ theme: { token }, $isEmpty }) => ($isEmpty ? token.paddingSM : token.paddingXS)}px;
  bottom: ${({ $isEmpty }) => ($isEmpty ? '4px' : '4px')};
  width: ${({ $isEmpty }) => ($isEmpty ? '24px' : '32px')};
  height: ${({ $isEmpty }) => ($isEmpty ? '24px' : '32px')};
  flex-shrink: 0;
  background-color: transparent;
  border: none;
  border-radius: ${({ theme: { token } }) => token.borderRadiusSM}px;
  color: ${({ disabled, theme: { token } }) => (disabled ? token.colorTextDisabled : token.colorPrimary)};
  font-size: ${({ $isEmpty }) => ($isEmpty ? '16px' : '18px')};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  padding: 0;
  z-index: 1;

  &:hover:not(:disabled) {
    background-color: ${({ theme: { token } }) => token.colorFillTertiary};
  }

  &:active:not(:disabled) {
    background-color: ${({ theme: { token } }) => token.colorFillSecondary};
  }

  svg {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

export const NewConversationButton = styled.button<{ theme: { token: GlobalToken } }>`
  width: 100%;
  background: ${({ theme: { token } }) => token.colorFillQuaternary};
  border: 1px solid ${({ theme: { token } }) => token.colorBorderSecondary};
  border-radius: ${({ theme: { token } }) => token.borderRadius}px;
  padding: ${({ theme: { token } }) => token.paddingSM}px;
  color: ${({ theme: { token } }) => token.colorText};
  font-size: ${({ theme: { token } }) => token.fontSize}px;
  cursor: pointer;
  margin-bottom: ${({ theme: { token } }) => token.marginSM}px;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ theme: { token } }) => token.colorFillTertiary};
    border-color: ${({ theme: { token } }) => token.colorPrimary};
  }
`;

export const SuggestedPromptsContainer = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${({ theme: { token } }) => token.marginSM}px;
  margin-bottom: 4px;
  width: 100%;
  max-width: 1200px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const SuggestedPromptButton = styled.button<{ theme: { token: GlobalToken } }>`
  background: ${({ theme: { token } }) => token.colorFillQuaternary};
  border: 1px solid ${({ theme: { token } }) => token.colorBorderSecondary};
  border-radius: ${({ theme: { token } }) => token.borderRadiusLG}px;
  padding: ${({ theme: { token } }) => token.paddingXS}px ${({ theme: { token } }) => token.paddingSM}px;
  color: ${({ theme: { token } }) => token.colorText};
  font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme: { token } }) => token.marginXXS}px;
  white-space: nowrap;
  height: auto;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  &:hover:not(:disabled) {
    background: ${({ theme: { token } }) => token.colorFillTertiary};
    border-color: ${({ theme: { token } }) => token.colorPrimary};
  }

  &:disabled {
    cursor: not-allowed;
  }

  .badge {
    padding: ${({ theme: { token } }) => token.paddingXXS}px ${({ theme: { token } }) => token.paddingSM}px;
    background: ${({ theme: { token } }) => token.colorError};
    border-radius: ${({ theme: { token } }) => token.borderRadiusSM}px;
    font-size: 9px;
    font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
    color: ${({ theme: { token } }) => token.colorTextLightSolid};
    text-transform: uppercase;
  }
`;
